import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import type {
  DashboardPerson,
  DashboardSummary,
} from "../../backend/api/dashboard";
import { useDashboard } from "../../backend/queries/dashboard";
import {
  ProjectStatusBadge,
  ProjectTypeBadge,
} from "../../components/domain-badges";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";

const currency = new Intl.NumberFormat(undefined, {
  currency: "AUD",
  currencyDisplay: "narrowSymbol",
  maximumFractionDigits: 0,
  style: "currency",
});

const emptyDashboard: DashboardSummary = {
  metrics: {
    total_projects: 0,
    active_projects: 0,
    people: 0,
    clients: 0,
    internal_users: 0,
    total_budget: 0,
  },
  status_counts: {},
  role_counts: {},
  recent_projects: [],
  recent_people: [],
};

const formatDate = (value: string | null) => {
  if (!value) return "Not set";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Not set";
  return date.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const fullName = (user: DashboardPerson | null) =>
  [user?.firstname, user?.lastname].filter(Boolean).join(" ") || "Unknown";

const Dashboard = () => {
  const navigate = useNavigate();
  const dashboardQuery = useDashboard();
  const dashboard = dashboardQuery.data || emptyDashboard;
  const loading = dashboardQuery.isPending;

  return (
    <div className="min-h-screen p-4 sm:p-6">
      <div className="mx-auto flex max-w-[1500px] flex-col gap-5">
        <h1 className="text-2xl font-semibold tracking-tight">Dashboard</h1>

        {dashboardQuery.error && (
          <Card className="border-destructive/40">
            <CardContent className="p-4 text-sm text-destructive">
              Dashboard data could not be loaded.
            </CardContent>
          </Card>
        )}

        <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <MetricCard
            label="Projects"
            value={loading ? "..." : dashboard.metrics.total_projects}
          />
          <MetricCard
            label="People"
            value={loading ? "..." : dashboard.metrics.people}
          />
          <MetricCard
            label="Active projects"
            value={loading ? "..." : dashboard.metrics.active_projects}
          />
          <MetricCard
            label="Budget"
            value={
              loading ? "..." : currency.format(dashboard.metrics.total_budget)
            }
          />
        </section>

        <Card className="rounded-xl border-black/5 shadow-sm">
          <CardHeader className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
            <CardTitle>Recent projects</CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/projects")}
            >
              View all
              <ArrowUpRight className="h-4 w-4" />
            </Button>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Project</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Start</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {dashboard.recent_projects.length ? (
                  dashboard.recent_projects.map((project) => (
                    <TableRow
                      key={project.id}
                      className="cursor-pointer"
                      tabIndex={0}
                      onClick={() => navigate(`/projects/${project.id}`)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          navigate(`/projects/${project.id}`);
                        }
                      }}
                    >
                      <TableCell>
                        <div className="space-y-1">
                          <div className="font-medium">
                            {project.relocating_from || "Origin"} to{" "}
                            {project.relocating_to || "Destination"}
                          </div>
                          <ProjectTypeBadge type={project.project_type} />
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">
                          {fullName(project.customer)}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {project.agent
                            ? `${fullName(project.agent)} assigned`
                            : "No agent"}
                        </div>
                      </TableCell>
                      <TableCell>
                        {formatDate(project.project_start_date)}
                      </TableCell>
                      <TableCell>
                        <ProjectStatusBadge status={project.status} />
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={4}
                      className="h-24 text-center text-muted-foreground"
                    >
                      No projects found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const MetricCard = ({ label, value }) => (
  <Card className="rounded-xl border-black/5 shadow-sm">
    <CardContent className="space-y-1 p-4">
      <p className="text-sm text-muted-foreground">{label}</p>
      <p className="text-2xl font-semibold tracking-tight tabular-nums">
        {value}
      </p>
    </CardContent>
  </Card>
);

export default Dashboard;
